/**
 * Compte connecté sans aucune entreprise en service (`no_company`, 25/10/2026) :
 * ce que l'écran de connexion ajoute aux invitations et à la création d'une
 * entreprise.
 * - Places de comptable en attente : la fiche du comptable les ouvre
 *   (`/compte/comptable`, jeton de compte transmis par la navigation).
 * - Supprimer son compte, action rare et définitive, dans le menu « … » :
 *   confirmée en recopiant SUPPRIMER, avec le jeton de compte de cette connexion
 *   (le serveur exige qu'elle vienne d'un facteur prouvé à l'instant).
 * Le jeton ne sert qu'une fois : après un refus, la connexion recommence.
 */
import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Briefcase, Trash2 } from 'lucide-react';
import { ApiError, api } from '../../core/api.js';
import { t } from '../../core/i18n.js';
import { forgetRememberedAccount } from '../../core/remembered-account.js';
import { ActionMenu } from '../../components/action-menu.js';
import { RecordIcon, RecordList, RecordRow } from '../../components/RecordRow.js';
import { ConfirmActionModal } from '../../components/ui.js';
import { Section } from '../../components/ui/Section.js';
import { DELETE_PHRASE, LOGIN_NOTICE_PARAM } from '../profile/account-closure.js';

/** Fiche du comptable (`AccountantProfilePage`), page de compte hors entreprise. */
const ACCOUNTANT_PATH = '/compte/comptable';

export function AdvisorPendingSection(props: { companies: readonly string[]; accountToken: string }): ReactNode {
  const navigate = useNavigate();
  if (props.companies.length === 0) return null;
  return (
    <Section title={t('compteEntreprises.comptableAttente')} hint={t('compteEntreprises.comptableAttenteAide')} flush>
      <RecordList label={t('compteEntreprises.comptableAttente')}>
        {props.companies.map((companyName) => (
          <li key={companyName}>
            <RecordRow
              onOpen={() => navigate(ACCOUNTANT_PATH, { state: { accountToken: props.accountToken } })}
              lead={<RecordIcon><Briefcase size={18} aria-hidden="true" /></RecordIcon>}
              title={companyName}
              lines={[t('compteEntreprises.comptableAttenteLigne')]}
            />
          </li>
        ))}
      </RecordList>
    </Section>
  );
}

export function DeleteCompanylessAccount(props: { accountToken: string; onExpired: (message: string) => void }): ReactNode {
  const [confirming, setConfirming] = useState(false);
  const remove = useMutation({
    mutationFn: () => api('/auth/account/delete', {
      method: 'POST', body: { confirmation: DELETE_PHRASE, accountToken: props.accountToken },
    }),
    onSuccess: () => {
      setConfirming(false);
      // Le compte n'existe plus : l'écran de connexion ne le propose plus.
      forgetRememberedAccount();
      window.location.assign(`/?${LOGIN_NOTICE_PARAM}=compte_supprime`);
    },
    onError: (error) => {
      // Jeton consommé ou trop ancien pour une suppression : se reconnecter.
      if (error instanceof ApiError && (error.code === 'unauthorized' || error.code === 'step_up_required')) {
        setConfirming(false);
        props.onExpired(error.message);
      }
    },
  });
  return (
    <>
      <ActionMenu
        label={t('compteEntreprises.autresActions')}
        items={[{
          label: t('compte.suppression.bouton'),
          icon: <Trash2 size={18} aria-hidden="true" />,
          tone: 'danger',
          onSelect: () => {
            remove.reset();
            setConfirming(true);
          },
        }]}
      />
      <ConfirmActionModal
        open={confirming}
        title={t('compte.suppression.confirmerTitre')}
        consequence={t('compte.suppression.consequence')}
        confirmLabel={t('compte.suppression.bouton')}
        tone="danger"
        confirmation={{ phrase: DELETE_PHRASE, label: t('compte.suppression.phrase') }}
        pending={remove.isPending}
        error={remove.error ? (remove.error as Error).message : undefined}
        onClose={() => setConfirming(false)}
        onConfirm={() => {
          if (!remove.isPending) remove.mutate();
        }}
      />
    </>
  );
}
